import React, { useState } from 'react';
import { IconButton, Popover, Typography, Box } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { format } from 'date-fns';

const EHR_METHODS = new Set(['ehr-discharge', 'ehr-sync']);

export function hasEhrImportSource(source) {
  if (!source || typeof source !== 'object') return false;
  return EHR_METHODS.has(source.importMethod);
}

function importedAtDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * ⓘ control for CMR rows imported from an EHR. Hidden when `source` is absent.
 */
export default function ProvenanceInfoButton({ source, label }) {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!hasEhrImportSource(source)) return null;

  const institutionName = source.institutionName || 'an institution';
  const clinicianName = source.clinicianName || null;
  const when = importedAtDate(source.importedAt);
  const dateLine = when ? `on ${format(when, 'MMMM d, yyyy')}` : null;

  return (
    <>
      <IconButton
        size="small"
        aria-label={label || `Record source: ${institutionName}`}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <InfoOutlined fontSize="small" color="action" />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography variant="body2">Added from {institutionName}</Typography>
          {clinicianName ? (
            <Typography variant="body2" color="text.secondary">
              {clinicianName}
            </Typography>
          ) : null}
          {dateLine ? (
            <Typography variant="body2" color="text.secondary">
              {dateLine}
            </Typography>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}
